import clsx from 'clsx';
import React from 'react';
import FormHelperText from '../../atoms/FormHelperText/FormHelperText';
import FormValidationMessage from '../../atoms/FormValidationMessage/FormValidationMessage';
import useFormFieldAria from '../../hooks/useFormFieldAria';

type FormFieldProps = {
  children: React.ReactElement;
  className?: string;
  error?: boolean;
  errorMessage?: string;
  helperText?: string;
  id?: string;
  isDisabled?: boolean;
  /**
   * Renders the helper text between the label and the input instead of below
   * the input (the DS default). Use only for the exceptional pattern where
   * the helper acts as an instructional hint shown BEFORE the field. Has no
   * effect when `helperText` is not provided.
   */
  isHelperTextAboveInput?: boolean;
  /**
   * Visually hides the `<label>` while keeping it in the DOM for screen readers.
   * Only takes effect when `label` is provided; ignored otherwise.
   */
  isLabelHidden?: boolean;
  isRequired?: boolean;
  /**
   * Visible label text. When the visual design hides the label (e.g. a search bar
   * with a magnifying-glass icon), prefer `label="..." + isLabelHidden` so the
   * `<label>` semantic is preserved in the DOM. Omit this prop only as a last
   * resort, in edge cases where no semantic label fits at all; in that case the
   * `<label>` element is not rendered and the consumer must provide the
   * accessible name on the child by other means.
   */
  label?: string;
  success?: boolean;
  successMessage?: string;
};

function FormField({
  children,
  className,
  error = false,
  errorMessage,
  helperText,
  id,
  isDisabled = false,
  isHelperTextAboveInput = false,
  isLabelHidden = false,
  isRequired = false,
  label,
  success = false,
  successMessage,
}: FormFieldProps): JSX.Element {
  const { ariaDescribedBy, ids } = useFormFieldAria({
    error,
    errorMessage,
    helperText,
    id,
    success,
    successMessage,
  });

  const shouldShowError = error && !!errorMessage;
  const shouldShowSuccess = !error && success && !!successMessage;

  // `validationState` is purely decorative: triggers the inline icon inside the child input.
  let validationState: 'error' | 'success' | undefined;

  if (shouldShowError) {
    validationState = 'error';
  } else if (shouldShowSuccess) {
    validationState = 'success';
  }

  const enhancedChild = React.cloneElement(children, {
    id: ids.baseId,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': error || undefined,
    'aria-required': isRequired || children.props.required || undefined,
    required: isRequired || children.props.required,
    disabled: isDisabled || children.props.disabled,
    ...(validationState && { validationState }),
  });

  const helperTextNode = helperText
    ? <FormHelperText id={ids.helperId} text={helperText} />
    : null;

  return (
    <div
      className={clsx(
        'form-field',
        className,
        { 'form-field--disabled': isDisabled },
      )}
    >
      {label && (
        <label
          id={ids.labelId}
          htmlFor={ids.baseId}
          className={clsx('form-field__label', { 'form-field__label--hidden': isLabelHidden })}
        >
          {label} {isRequired && <sup aria-hidden="true">*</sup>}
        </label>
      )}

      {isHelperTextAboveInput && helperTextNode}

      {enhancedChild}

      {!isHelperTextAboveInput && helperTextNode}

      {shouldShowError && (
        <FormValidationMessage id={ids.validationId} type="error" text={errorMessage} />
      )}

      {shouldShowSuccess && (
        <FormValidationMessage id={ids.validationId} type="success" text={successMessage} />
      )}
    </div>
  );
}

export default FormField;
